import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { NativeSelect } from '@/components/ui/Input';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';
import { keys, tenantKey } from '@/lib/queryKeys';
import { toast } from '@/hooks/useToast';
import { fetchCopilotGaps, reviewCopilotGap, type CopilotGap, type CopilotGapReason } from './api';

const REASON: Record<CopilotGapReason, string> = {
  no_tool: 'No measure for this',
  unknown_metric: 'Asked for a measure that doesn’t exist',
  unsupported_breakdown: 'Breakdown not available',
  unsupported_filter: 'Filter not available',
};

/**
 * Questions ClintoBytes Copilot could not answer — the backlog of measures to
 * add to its metrics catalogue (utils/copilot/metrics.js). Questions are
 * stored with phone numbers and ID numbers masked; who asked is shown by role.
 */
export function CopilotGapsSection() {
  const qc = useQueryClient();
  const [window, setWindow] = useState('30d');
  const [show, setShow] = useState<'open' | 'all'>('open');
  const gaps = useQuery({ queryKey: keys.admin.aiActivityCopilotGaps(window, show), queryFn: () => fetchCopilotGaps(window, show) });
  const review = useMutation({
    mutationFn: (id: number) => reviewCopilotGap(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey('admin', 'ai-activity', 'copilot-gaps') }),
    onError: (e) => toast({ title: 'Couldn’t mark it reviewed', description: (e as Error).message, variant: 'danger' }),
  });

  const columns: DataTableColumn<CopilotGap>[] = [
    { key: 'when', header: 'Asked', widthClassName: 'w-[120px]', className: 'text-ink-muted',
      render: (g) => new Date(g.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) },
    { key: 'q', header: 'Question', render: (g) => <span className="whitespace-pre-wrap text-ink">{g.question}</span> },
    { key: 'why', header: 'Why it couldn’t answer', widthClassName: 'w-[220px]', render: (g) => (
      <span className="flex flex-col">
        <span>{REASON[g.reason]}</span>
        {g.detail && <span className="text-[11.5px] text-ink-muted">{g.detail}</span>}
      </span>
    ) },
    { key: 'who', header: 'Asked by', widthClassName: 'w-[150px]', className: 'text-ink-muted',
      render: (g) => [g.role, g.branch_name].filter(Boolean).join(' · ') || '—' },
    { key: 'act', header: '', widthClassName: 'w-[130px]', align: 'right', render: (g) => (g.reviewed_at
      ? <Badge variant="neutral">Reviewed</Badge>
      : <Button size="sm" variant="ghost" loading={review.isPending && review.variables === g.id} onClick={(e) => { e.stopPropagation(); review.mutate(g.id); }}>Mark reviewed</Button>) },
  ];

  return (
    <section className="flex flex-col gap-2" aria-labelledby="copilot-gaps-heading">
      <div className="flex flex-wrap items-center gap-2">
        <div id="copilot-gaps-heading" className="text-[13px] font-semibold text-ink">Questions the Copilot couldn’t answer</div>
        {gaps.data && gaps.data.total > 0 && <Badge variant="warn">{gaps.data.total}</Badge>}
        <div className="ml-auto flex items-center gap-2">
          <NativeSelect value={show} onChange={(e) => setShow(e.target.value as 'open' | 'all')} className="w-auto text-xs" aria-label="Which questions">
            <option value="open">Not reviewed</option>
            <option value="all">All</option>
          </NativeSelect>
          <NativeSelect value={window} onChange={(e) => setWindow(e.target.value)} className="w-auto text-xs" aria-label="Questions from">
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </NativeSelect>
        </div>
      </div>
      <p className="text-[12px] text-ink-muted">
        Each one is a count or report staff wanted that the Copilot has no measure for yet: the list of measures to add next.
        Phone and ID numbers are removed before a question is kept. Mark one reviewed once it is added or decided out of scope.
      </p>
      <DataTable
        columns={columns}
        rows={gaps.data?.items ?? []}
        rowKey={(g) => g.id}
        isLoading={gaps.isLoading}
        isError={gaps.isError}
        onRetry={() => gaps.refetch()}
        emptyLabel={show === 'open' ? 'Nothing waiting — every question in this period was answered or reviewed.' : 'No unanswered questions in this period.'}
        density="compact"
      />
    </section>
  );
}
